import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { LinkedinTypes } from 'enums';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import PaiSpinningLoader from 'features/ui/base-components/pai-spinning-loader';
import PaiButton from 'features/ui/base-components/pai-button';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface ItemProps {
  linkedinAccount: LinkedinAccount;
}

const LinkedinAccountItem: React.FC<ItemProps> = ({ linkedinAccount }) => {
  const { classes } = useStyles();

  const { userEmail, liProductType } = linkedinAccount;

  const mapProductType = (productType: LinkedinTypes) => {
    switch (productType) {
      case LinkedinTypes.LINKEDIN:
        return 'LinkedIn';
      case LinkedinTypes.SALES_NAV:
        return 'Sales Navigator';
      case LinkedinTypes.RECRUITER:
        return 'Recruiter';
      default:
        return 'LinkedIn';
    }
  };

  return (
    <Stack className={classes.linkedinAccountItem} direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
      <Stack direction={'row'} alignItems={'center'} gap={1}>
        <LinkedInIcon className={classes.icon} />
        <Typography className={classes.primaryText}>{userEmail}</Typography>
      </Stack>
      <Typography className={classes.secondaryText}>{mapProductType(liProductType)}</Typography>
    </Stack>
  );
};

interface Props {
  open: boolean;
  loading: boolean;
  linkedinAccounts: LinkedinAccount[];

  handleClose: () => void;
}

const LinkedinAccountPreviewDialog: React.FC<Props> = ({ open, loading, linkedinAccounts, handleClose }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'sm'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('linkedinAccountOverview')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {loading ? (
          <PaiSpinningLoader />
        ) : (
          <>
            <Typography className={classes.subtitleText} sx={{ mb: '16px' }}>
              {t('linkedinAccountOverviewSubtitle')}
            </Typography>
            <Stack gap={1}>
              {linkedinAccounts.map((linkedinAccount) => (
                <LinkedinAccountItem key={linkedinAccount._id} linkedinAccount={linkedinAccount} />
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiButton text={t('close')} border={'rounded'} size={'small'} action={handleClose} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  linkedinAccountItem: {
    backgroundColor: '#FFF',
    borderRadius: '4px',
    padding: '4px 8px',
    border: `1px solid ${theme.palette.divider}`,
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.primary.main,
  },
  primaryText: {
    fontSize: theme.typography.pxToRem(14),
  },
  secondaryText: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
  },
  subtitleText: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default LinkedinAccountPreviewDialog;
